import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Attachment } from "utils/usageUtils";

type ImmediateUploadConfig = {
  upload: (attachments: Attachment[]) => Promise<void>;
  onSuccess?: (uploaded: Attachment[]) => void | Promise<void>;
  onError?: (error: Error) => void;
  onLoadingChange?: (loading: boolean) => void;
};

function useImmediateAttachmentUpload(config?: ImmediateUploadConfig) {
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const currentConfig = configRef.current;
    if (!currentConfig || pendingAttachments.length === 0) {
      return;
    }

    const attachmentsToUpload = pendingAttachments;
    setIsUploading(true);
    currentConfig.onLoadingChange?.(true);

    currentConfig
      .upload(attachmentsToUpload)
      .then(async () => {
        setPendingAttachments([]);
        await currentConfig.onSuccess?.(attachmentsToUpload);
      })
      .catch((err) => {
        setPendingAttachments((current) => {
          return current.filter(
            (attachment) => !attachmentsToUpload.includes(attachment)
          );
        });
        currentConfig.onError?.(err as Error);
      })
      .finally(() => {
        setIsUploading(false);
        currentConfig.onLoadingChange?.(false);
      });
  }, [pendingAttachments]);

  return {
    pendingAttachments,
    setPendingAttachments,
    isUploading,
  } satisfies {
    pendingAttachments: Attachment[];
    setPendingAttachments: Dispatch<SetStateAction<Attachment[]>>;
    isUploading: boolean;
  };
}

export { useImmediateAttachmentUpload };
export type { ImmediateUploadConfig };
